import { isEqual } from 'lodash';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useMaterialPage } from '../MaterialPage/hooks';

import { ProductReplacementModal } from '~/UI/components/organisms/ProductReplacementModal';
import { EDGE_BRAND_RULES } from '~/core/constants';
import { defaultColorSort } from '~/core/constants/sortAndFilter';
import { FiltersBySection, StepName, UserDesignOperation } from '~/core/enums';
import { useEquivalentProductMatching } from '~/core/hooks/useEquivalentProductMatching/useEquivalentProductMatching';
import useLazyLoad from '~/core/hooks/useLazyLoad';
import { useRegisterUserAction } from '~/core/hooks/useRegisterUserAction';
import { useResetSteps } from '~/core/hooks/useResetSteps';
import { ColorModel } from '~/core/models';
import { SelectedFilterOptions } from '~/core/types/sortAndFilter';
import { getProductDescription } from '~/core/utils';
import { sortColorsBy, updateListAndFilters } from '~/core/utils/sortAndFilter';
import { useDrawingShapeContext } from '~/legacy/easy-estimator-page/context';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import {
  selectColorsByProductId,
  selectRestorableColorId,
  selectSelectedColor,
  selectSelectedColorId,
  selectUsableColorIds,
  selectUsableColors,
  updateColorsIds,
  updateRestorableColorId,
  updateSelectedColorId,
} from '~/redux/slices/color';
import { updateNeedRedrawCanvas } from '~/redux/slices/drawingShape';
import { selectSelectedMaterial } from '~/redux/slices/material';
import {
  selectFilters,
  selectSelectedFilters,
  selectSortBy,
  updateFilters,
  updateSortBy,
} from '~/redux/slices/sortAndFilter';
import { enableStep } from '~/redux/slices/stepper';
import PageSubtitle from '~atoms/PageSubtitle';
import ColorVizConfContent from '~molecules/ColorVizConfContent';
import ColorVizContent from '~molecules/ColorVizContent';
import SkeletonCard from '~molecules/SkeletonCard';
import ColorsList from '~organisms/ColorsList';
import SlabViewer from '~organisms/SlabViewer';
import SortAndFiltersSection from '~organisms/SortAndFiltersSection';
import Colors from '~templates/Colors';

const colorFilterComponent = FiltersBySection.COLOR;

const ColorPage = () => {
  const dispatch = useAppDispatch();
  const subtitleId = useId();
  const { t } = useTranslation();
  const colors = useAppSelector(selectUsableColors);
  const colorIds = useAppSelector(selectUsableColorIds);
  const colorsById = useAppSelector(selectColorsByProductId);
  const selectedColor = useAppSelector(selectSelectedColor);
  const selectedColorId = useAppSelector(selectSelectedColorId);
  const selectedMaterial = useAppSelector(selectSelectedMaterial);
  const restorableColorId = useAppSelector(selectRestorableColorId);
  const selectedSort = useAppSelector(selectSortBy(colorFilterComponent));
  const filters = useAppSelector(selectFilters(colorFilterComponent));
  const selectedFilters = useAppSelector(
    selectSelectedFilters(colorFilterComponent)
  );
  const { removedProducts, findAndSetRemovedProductsByColorId } =
    useMaterialPage();

  const [candidateColor, setCandidateColor] = useState<
    ColorModel | undefined
  >();
  const [previewColor, setPreviewColor] = useState<ColorModel>();
  const [showColorVisualizer, setShowColorVisualizer] = useState(false);
  const [isSlabViewerVisible, setIsSlabViewerVisible] = useState(false);
  const [isColorModalVisible, setIsColorModalVisible] = useState(false);
  const [colorVizImage, setColorVizImage] = useState('');
  const [colorVizName, setColorVizName] = useState('');
  const { visibleItems, setObserverTarget } = useLazyLoad({
    itemCount: colors.length,
  });
  const allColors = Object.values(colorsById);
  const updatedColorIds = colorIds.join(',');
  const [visibleColors, skeletonColors] = [
    colors.slice(0, visibleItems),
    colors.slice(visibleItems),
  ];
  const fstColorId = colors.slice(0, 1)[0]?.productId;
  const isRestorableColorIdInList =
    restorableColorId && restorableColorId in colorsById;
  const isSelectedColorIdInList =
    selectedColorId && selectedColorId in colorsById;

  const {
    getters: { areThereShapesInTheDrawingArea },
  } = useDrawingShapeContext();
  const { restoreDataWithoutAutoNavigation } = useResetSteps();
  const { replaceProductsWithEquivalents } = useEquivalentProductMatching();

  const { registerUserAction } = useRegisterUserAction();

  const applicableRule = EDGE_BRAND_RULES.find(
    (rule) =>
      selectedMaterial?.description?.match(rule.material) &&
      selectedColor?.brand?.match(rule.colorBrand)
  );

  const handleAddUserDesignOperation = () => {
    if (areThereShapesInTheDrawingArea) {
      dispatch(updateNeedRedrawCanvas(true));
      registerUserAction(UserDesignOperation.MODIFY_COLOR);
    }
  };

  const handleChangeColorFromModal = () => {
    setIsColorModalVisible(false);

    // find the color name from the visualizer and select it (colorVizName)
    const color = allColors.find((color) => color.name === colorVizName);

    if (color) {
      handleAddUserDesignOperation();
      dispatch(updateSelectedColorId(color.productId));
      if (colorVizImage) {
        color.visualizerImg = colorVizImage;
      }
    }
  };

  const handleFiltersChange = (options: SelectedFilterOptions) => {
    const sortedColors = sortColorsBy(selectedSort, allColors);
    const { ids, filters } = updateListAndFilters(sortedColors, options);

    dispatch(
      updateFilters({
        component: colorFilterComponent,
        filters,
        selectedFilters: options,
      })
    );
    dispatch(updateColorsIds({ byId: colorsById, usableIds: ids }));
  };

  useEffect(() => {
    [...document.querySelectorAll('.skeleton')]
      .slice(0, 1)
      .map((element) => setObserverTarget(element));
  }, [visibleItems, updatedColorIds, setObserverTarget]);

  useEffect(() => {
    if (!isSelectedColorIdInList) {
      const colorId = isRestorableColorIdInList
        ? restorableColorId
        : fstColorId;

      if (colorId) {
        dispatch(updateSelectedColorId(colorId));
      }
    }
  }, [
    dispatch,
    fstColorId,
    restorableColorId,
    isRestorableColorIdInList,
    isSelectedColorIdInList,
  ]);

  useEffect(() => {
    if (selectedColorId) {
      dispatch(enableStep(StepName.designShape));
    }
  }, [dispatch, selectedColorId]);

  useEffect(() => {
    if (restorableColorId) {
      dispatch(updateRestorableColorId(undefined));
    }
  }, [dispatch, restorableColorId]);

  useEffect(() => {
    if (selectedSort === undefined) {
      dispatch(
        updateSortBy({
          component: colorFilterComponent,
          sortBy: defaultColorSort,
        })
      );
    }

    const ids = sortColorsBy(selectedSort, colors).map(
      (color) => color.productId
    );

    if (!isEqual(colorIds, ids)) {
      dispatch(
        updateColorsIds({
          byId: colorsById,
          usableIds: ids,
        })
      );
    }
  }, [colorIds, colors, colorsById, dispatch, selectedSort]);

  const resetCandidateColor = () => {
    setCandidateColor(undefined);
  };

  const changeToColor = (newColor: ColorModel) => async () => {
    resetCandidateColor();
    await replaceProductsWithEquivalents({ color: newColor });
    handleAddUserDesignOperation();
    restoreDataWithoutAutoNavigation(() => {
      dispatch(updateSelectedColorId(newColor.productId));
    });
  };

  return (
    <Colors
      subtitleSlot={
        <PageSubtitle id={subtitleId}>
          {t('SCROLLABLE_AREA.COLOR.DESCRIPTION')}
        </PageSubtitle>
      }
      filtersSlot={
        <SortAndFiltersSection
          title="Sort & Filters"
          data={{ filters }}
          selectedFilterOptions={selectedFilters}
          onChange={handleFiltersChange}
          component={FiltersBySection.COLOR}
        />
      }
    >
      <ColorsList
        aria={{ 'aria-labelledby': subtitleId }}
        data={{ colors: visibleColors }}
        selectedColorId={selectedColorId}
        setIsSlabViewerVisible={setIsSlabViewerVisible}
        setPreviewColor={setPreviewColor}
        showColorVisualizer={showColorVisualizer}
        setShowColorVisualizer={setShowColorVisualizer}
        onSelect={async (productId) => {
          const removedProducts =
            areThereShapesInTheDrawingArea && applicableRule
              ? await findAndSetRemovedProductsByColorId(productId)
              : [];

          if (removedProducts.length > 0) {
            setCandidateColor(colorsById[productId]);
          } else {
            handleAddUserDesignOperation();
            dispatch(updateSelectedColorId(productId));
          }
        }}
      >
        {skeletonColors.map((color) => (
          <SkeletonCard key={`skeleton-${color.productId}`} type="color" />
        ))}
      </ColorsList>
      {previewColor && (
        <SlabViewer
          visible={isSlabViewerVisible}
          previewColor={previewColor}
          materialName={selectedMaterial?.description ?? ''}
          onCancel={() => setIsSlabViewerVisible(false)}
        />
      )}
      <ColorVizContent
        showColorVisualizer={showColorVisualizer}
        selectedColor={selectedColor}
        setShowColorVisualizer={setShowColorVisualizer}
        setColorVizImage={setColorVizImage}
        colorVizName={colorVizName}
        setColorVizName={setColorVizName}
        showColorModal={() => setIsColorModalVisible(true)}
      />
      <ColorVizConfContent
        isModalVisible={isColorModalVisible}
        selectedColor={selectedColor}
        onConfirmChangingColorFromModal={handleChangeColorFromModal}
        onCancelChangingColorFromModal={() => setIsColorModalVisible(false)}
        colorVizName={colorVizName}
      />
      {candidateColor && (
        <ProductReplacementModal
          newProductName={getProductDescription(candidateColor)}
          typeOfProduct="color"
          productsToReplace={removedProducts}
          onCancel={resetCandidateColor}
          onConfirm={changeToColor(candidateColor)}
        />
      )}
    </Colors>
  );
};

export default ColorPage;
